import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, User, KeyRound, Users } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, formatApiErrorDetail } from "../../lib/operatorWalk";

export default function AdminSettings() {
  const { user, authHeader, updateUser } = usePortalAuth();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", email: user.email || "" });
  }, [user]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${OW_API}/admin/ow/users`, { headers: authHeader });
      setUsers(res.data.users || []);
    } catch {
      toast.error("Could not load users.");
    } finally {
      setLoadingUsers(false);
    }
  }, [authHeader]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await axios.patch(`${OW_API}/auth/profile`, profile, { headers: authHeader });
      updateUser(res.data.user);
      toast.success("Profile updated.");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("The new passwords do not match.");
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error("The new password must be at least 8 characters.");
      return;
    }
    setSavingPw(true);
    try {
      await axios.post(
        `${OW_API}/auth/change-password`,
        { current_password: pw.current_password, new_password: pw.new_password },
        { headers: authHeader }
      );
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setSavingPw(false);
    }
  };

  const inputCls = "mt-1.5 w-full border border-brand-200 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-500";

  return (
    <AdminLayout title="Settings" subtitle="Manage your admin profile and account security.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile */}
        <form onSubmit={saveProfile} className="border border-ink-line bg-white p-6" data-testid="settings-profile-form">
          <div className="flex items-center gap-2 mb-5">
            <User className="w-4 h-4 text-brand-500" />
            <h2 className="font-serif text-xl text-brand-700">Profile</h2>
          </div>
          <label className="block mb-4">
            <span className="text-xs uppercase tracking-wide text-brand-500">Name</span>
            <input
              value={profile.name}
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
              className={inputCls}
              required
              data-testid="settings-name-input"
            />
          </label>
          <label className="block mb-5">
            <span className="text-xs uppercase tracking-wide text-brand-500">Email</span>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              className={inputCls}
              required
              data-testid="settings-email-input"
            />
          </label>
          <button
            type="submit"
            disabled={savingProfile}
            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="settings-save-profile"
          >
            {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />}
            Save profile
          </button>
        </form>

        {/* Change password */}
        <form onSubmit={changePassword} className="border border-ink-line bg-white p-6" data-testid="settings-password-form">
          <div className="flex items-center gap-2 mb-5">
            <KeyRound className="w-4 h-4 text-brand-500" />
            <h2 className="font-serif text-xl text-brand-700">Change password</h2>
          </div>
          <label className="block mb-4">
            <span className="text-xs uppercase tracking-wide text-brand-500">Current password</span>
            <input
              type="password"
              value={pw.current_password}
              onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
              className={inputCls}
              required
              autoComplete="current-password"
              data-testid="settings-current-password"
            />
          </label>
          <label className="block mb-4">
            <span className="text-xs uppercase tracking-wide text-brand-500">New password</span>
            <input
              type="password"
              value={pw.new_password}
              onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
              className={inputCls}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="settings-new-password"
            />
          </label>
          <label className="block mb-5">
            <span className="text-xs uppercase tracking-wide text-brand-500">Confirm new password</span>
            <input
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              className={inputCls}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="settings-confirm-password"
            />
          </label>
          <button
            type="submit"
            disabled={savingPw}
            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="settings-save-password"
          >
            {savingPw && <Loader2 className="w-4 h-4 animate-spin" />}
            Update password
          </button>
        </form>
      </div>

      {/* Users (read-only) */}
      <div className="mt-8 border border-ink-line bg-white" data-testid="settings-users">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-ink-line">
          <Users className="w-4 h-4 text-brand-500" />
          <h2 className="font-serif text-xl text-brand-700">Accounts</h2>
        </div>
        {loadingUsers ? (
          <div className="py-12 flex items-center justify-center text-ink-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand-50/50 border-b border-ink-line text-left">
                  <th className="px-6 py-3 font-medium text-brand-700">Name</th>
                  <th className="px-6 py-3 font-medium text-brand-700">Email</th>
                  <th className="px-6 py-3 font-medium text-brand-700">Role</th>
                  <th className="px-6 py-3 font-medium text-brand-700">Company</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-ink-line last:border-b-0" data-testid={`settings-user-${u.email}`}>
                    <td className="px-6 py-3 text-brand-700">{u.name || "Not set"}</td>
                    <td className="px-6 py-3 text-ink-soft">{u.email}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${u.role === "admin" ? "bg-brand-700 text-white" : "bg-brand-100 text-brand-700 border border-brand-200"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-ink-soft">{u.company_name || "Not applicable"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
